import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFilter } from 'react-icons/fa';
import API from '../api/index.js';
import FormSelect from '../components/FormSelect.jsx';
import { getLibraries } from '../services/api.js';
import { useToast } from '../context/ToastContext.jsx';
import { useUrlFilters } from '../hooks/useUrlFilters.js';
import '../styles/pages/employees.css';

const emptyForm = {
  name: '',
  email: '',
  password: '',
  role: 'admin',
  status: 'active',
  library_ids: []
};

const getLibraryId = (library) => library.library_id || library.id || library._id;
const getUserId = (user) => user.user_id || user._id || user.id;
const sameId = (left, right) => String(left) === String(right);

function LibraryAccessOptions({ libraries, selectedIds, onToggle, onToggleAll, idPrefix }) {
  const allSelected = libraries.length > 0 && libraries.every(library =>
    selectedIds.some(id => sameId(id, getLibraryId(library)))
  );

  if (!libraries.length) return <p className="employee-empty-libraries">No libraries available.</p>;

  return (
    <div className="employee-library-options">
      <label className="employee-library-option select-all">
        <input type="checkbox" checked={allSelected} onChange={(event) => onToggleAll(event.target.checked)} />
        <span>Assign All Libraries</span>
      </label>
      {libraries.map((library) => {
        const id = getLibraryId(library);
        const optionId = `${idPrefix}-${id}`;
        return (
          <label className="employee-library-option" htmlFor={optionId} key={id}>
            <input
              id={optionId}
              type="checkbox"
              checked={selectedIds.some(selectedId => sameId(selectedId, id))}
              onChange={() => onToggle(id)}
            />
            <span>{library.library_name || library.name || 'Unnamed library'}</span>
          </label>
        );
      })}
    </div>
  );
}

function formatDate(dateValue) {
  if (!dateValue) return 'Never';
  const value = String(dateValue).trim();
  if (!value || ['never', '-', 'invalid date'].includes(value.toLowerCase())) return 'Never';

  let date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    date = new Date(`${value}T00:00:00`);
  } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(value)) {
    date = new Date(value.replace(' ', 'T'));
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime()) || date.getUTCFullYear() < 2000 || date.getUTCFullYear() > 2100) {
    return 'Never';
  }
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

function EmployeesSkeleton({ canManageUsers, onAddEmployee }) {
  return (
    <div className="employees-page employees-skeleton" aria-label="Loading employees" aria-busy="true">
      <header className="employees-header">
        <div className="employees-title-wrap">
          <span className="employees-heading-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM20 8v6m3-3h-6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
          </span>
          <div className="employee-skeleton-heading">
            <h1>Employee Management</h1>
            <p>Manage employee accounts, status, and library access.</p>
          </div>
        </div>
        {canManageUsers && <button type="button" className="employee-add-button" onClick={onAddEmployee}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>Add Employee</button>}
      </header>

      <section className="employee-stats" aria-hidden="true">
        {[1, 2, 3].map((item) => (
          <div className="panel employee-stat employee-skeleton-stat" key={item}>
            <span className="employee-skeleton-block employee-skeleton-stat-label" />
            <span className="employee-skeleton-block employee-skeleton-stat-value" />
          </div>
        ))}
      </section>

      <section className="employee-filter-card employee-skeleton-filter" aria-hidden="true">
        <div className="employee-skeleton-block employee-skeleton-filter-title" />
        <div className="employee-skeleton-filter-controls">
          {[1, 2, 3, 4, 5].map((item) => <span className="employee-skeleton-block" key={item} />)}
        </div>
        <div className="employee-skeleton-block employee-skeleton-filter-footer" />
      </section>

      <section className="panel table-panel employee-skeleton-table" aria-hidden="true">
        <div className="employee-table-heading">
          <span className="employee-skeleton-block employee-skeleton-table-title" />
          <span className="employee-skeleton-block employee-skeleton-table-count" />
        </div>
        <div className="employee-skeleton-table-head">
          {Array.from({ length: 8 }, (_, index) => <span className="employee-skeleton-block" key={index} />)}
        </div>
        {Array.from({ length: 6 }, (_, index) => (
          <div className="employee-skeleton-row" key={index}>
            <span className="employee-skeleton-block employee-skeleton-avatar" />
            {Array.from({ length: 6 }, (_, cell) => <span className="employee-skeleton-block" key={cell} />)}
            <span className="employee-skeleton-actions">
              <i className="employee-skeleton-block" />
              <i className="employee-skeleton-block" />
              <i className="employee-skeleton-block" />
            </span>
          </div>
        ))}
        <div className="employee-skeleton-pagination">
          {[1, 2, 3, 4, 5].map((item) => <span className="employee-skeleton-block" key={item} />)}
        </div>
      </section>
    </div>
  );
}

export function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [accessUser, setAccessUser] = useState(null);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState([]);
  const [accessSaving, setAccessSaving] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { showToast, showConfirmationToast } = useToast();

  useUrlFilters([
    { key: 'search', value: search, setValue: setSearch, defaultValue: '' },
    { key: 'status', value: statusFilter, setValue: setStatusFilter, defaultValue: 'all' },
    { key: 'role', value: roleFilter, setValue: setRoleFilter, defaultValue: 'all' },
    { key: 'library', value: libraryFilter, setValue: setLibraryFilter, defaultValue: 'all' },
  ]);

  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const usersResponse = await API.get('/api/users');
      const data = usersResponse.data;
      const userList = Array.isArray(data) ? data : data?.data || data?.users || data?.results || [];
      setEmployees(Array.isArray(userList) ? userList : []);
    } catch (error) {
      console.error('Failed to load employees:', error);
      showToast(error.response?.data?.error || 'Failed to load employees.', 'error');
    }

    try {
      const librariesResponse = await getLibraries();
      const libraryList = Array.isArray(librariesResponse)
        ? librariesResponse
        : librariesResponse?.data || librariesResponse?.results || librariesResponse?.libraries || [];
      setLibraries(Array.isArray(libraryList) ? libraryList : []);
    } catch (error) {
      console.error('Failed to load employee library access options:', error);
      showToast('Employees loaded, but library options could not be loaded.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userinfo') || '{}');
      setCurrentUserRole(String(userInfo.role || '').toLowerCase().replace(/[\s_-]/g, ''));
    } catch (error) {
      console.error('Failed to read current user role:', error);
      setCurrentUserRole('');
    }
    loadData();
  }, [loadData]);

  const libraryNames = useMemo(() => {
    const namesById = new Map();
    libraries.forEach((library) => {
      const id = getLibraryId(library);
      if (id !== undefined && id !== null) namesById.set(String(id), library.library_name || library.name);
    });
    return namesById;
  }, [libraries]);

  const availableLibraryIds = useMemo(
    () => libraries
      .map(getLibraryId)
      .filter(id => id !== undefined && id !== null),
    [libraries]
  );

  const enrichedEmployees = useMemo(() => employees.map((employee) => {
    const ids = Array.isArray(employee.library_ids)
      ? employee.library_ids
      : employee.library_id || employee.libraryId
        ? [employee.library_id || employee.libraryId]
        : [];
    const names = Array.isArray(employee.library_names)
      ? employee.library_names
      : ids.map(id => libraryNames.get(String(id))).filter(Boolean);

    // An employee with every library should read as "All Libraries" instead of
    // only the primary library name returned by the API.
    const hasAllLibraries = availableLibraryIds.length > 0
      && availableLibraryIds.every(libraryId => ids.some(id => sameId(id, libraryId)));

    let libraryDisplay;
    if (!ids.length) {
      libraryDisplay = 'No Access';
    } else if (hasAllLibraries) {
      libraryDisplay = 'All Libraries';
    } else if (names.length > 1) {
      libraryDisplay = names.join(', ');
    } else {
      libraryDisplay = names[0] || employee.library_name || employee.libraryName || 'No Access';
    }

    const id = getUserId(employee) || employee.email;
    return {
      ...employee,
      _uid: id,
      _libraryIds: ids,
      _libraryNames: names,
      _hasAllLibraries: hasAllLibraries,
      library_name_display: libraryDisplay,
      created_date_display: formatDate(employee.created_at || employee.createdAt),
      last_login_display: formatDate(employee.last_login || employee.lastLogin)
    };
  }), [employees, libraryNames, availableLibraryIds]);

  const activeCount = enrichedEmployees.filter(employee =>
    String(employee.status || '').toLowerCase() === 'active'
  ).length;
  const inactiveCount = enrichedEmployees.filter(employee =>
    String(employee.status || '').toLowerCase() === 'inactive'
  ).length;
  const canManageUsers = currentUserRole === 'tenantowner';
  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    return enrichedEmployees.filter((employee) => {
      const matchesSearch = !term || [
        employee.name,
        employee.username,
        employee.email,
        employee.library_name_display
      ].some(value => String(value || '').toLowerCase().includes(term));
      const matchesStatus = statusFilter === 'all' || String(employee.status || 'active').toLowerCase() === statusFilter;
      const matchesRole = roleFilter === 'all' || String(employee.role || '').toLowerCase() === roleFilter;
      const matchesLibrary = libraryFilter === 'all'
        || employee._libraryIds.some(id => sameId(id, libraryFilter))
        || (libraryFilter === 'none' && employee._libraryIds.length === 0);
      return matchesSearch && matchesStatus && matchesRole && matchesLibrary;
    });
  }, [enrichedEmployees, search, statusFilter, roleFilter, libraryFilter]);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const visibleEmployees = filteredEmployees.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const hasActiveFilters = Boolean(search.trim()) || statusFilter !== 'all' || roleFilter !== 'all' || libraryFilter !== 'all';

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, roleFilter, libraryFilter, itemsPerPage]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const validateName = (name) => {
    if (!name.trim()) return 'Name is required.';
    if (/\d/.test(name)) return 'Name should not contain numeric characters.';
    if (name.trim().length < 2) return 'Name must be at least 2 characters long.';
    return '';
  };

  const validateEmail = (email) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return 'Email is required.';
    if (!/^[^\s@]+@(?:[a-zA-Z0-9-]+\.)+com$/i.test(trimmedEmail)) {
      return 'Enter a valid email address ending in .com.';
    }
    const localPart = trimmedEmail.split('@')[0];
    if (localPart.length >= 3 && new Set(localPart).size === 1) return 'Please enter a valid email address.';
    return '';
  };

  const openAdd = () => {
    setForm({ ...emptyForm });
    setEditing('new');
  };

  const openEdit = (employee) => {
    setForm({
      name: employee.name || '',
      email: employee.email || '',
      password: '',
      role: employee.role || 'admin',
      status: String(employee.status || 'active').toLowerCase(),
      library_ids: [...employee._libraryIds]
    });
    setEditing(employee);
  };

  const toggleFormLibrary = (id) => {
    setForm(current => ({
      ...current,
      library_ids: current.library_ids.some(existingId => sameId(existingId, id))
        ? current.library_ids.filter(existingId => !sameId(existingId, id))
        : [...current.library_ids, id]
    }));
  };

  const toggleAllFormLibraries = (checked) => {
    setForm(current => ({
      ...current,
      library_ids: checked ? libraries.map(getLibraryId).filter(id => id !== undefined && id !== null) : []
    }));
  };

  const saveEmployee = async (event) => {
    event.preventDefault();
    const nameError = validateName(form.name);
    const emailError = validateEmail(form.email);
    if (nameError || emailError) {
      showToast(nameError || emailError, 'error');
      return;
    }
    if (editing === 'new' && form.password.length < 6) {
      showToast('Password must be at least 6 characters.', 'error');
      return;
    }
    if (editing !== 'new' && form.password && form.password.length < 6) {
      showToast('Password must be at least 6 characters.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: 'admin',
        status: form.status
      };
      // Library access is owner-only. Sending an unchanged list from an admin
      // would be rejected by the API, so omit it unless this is a new employee.
      if (editing === 'new' || canManageUsers) {
        payload.library_ids = form.library_ids;
      }
      if (form.password) payload.password = form.password;
      if (editing === 'new') await API.post('/api/users', payload);
      else await API.put(`/api/users/${getUserId(editing)}`, payload);

      showToast(editing === 'new' ? 'Employee created successfully.' : 'Employee updated successfully.', 'success');
      setEditing(null);
      setForm({ ...emptyForm });
      await loadData(false);
    } catch (error) {
      console.error('Failed to save employee:', error);
      showToast(error.response?.data?.error || error.response?.data?.message || 'Failed to save employee.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openLibraryAccess = (employee) => {
    setAccessUser(employee);
    setSelectedLibraryIds([...employee._libraryIds]);
  };

  const toggleAccessLibrary = (id) => {
    setSelectedLibraryIds(current => current.some(existingId => sameId(existingId, id))
      ? current.filter(existingId => !sameId(existingId, id))
      : [...current, id]);
  };

  const saveLibraryAccess = async () => {
    if (!accessUser) return;
    setAccessSaving(true);
    try {
      await API.put(`/api/users/${getUserId(accessUser)}`, { library_ids: selectedLibraryIds });
      showToast('Library access updated successfully.', 'success');
      setAccessUser(null);
      await loadData(false);
    } catch (error) {
      console.error('Failed to update employee library access:', error);
      showToast(error.response?.data?.error || 'Failed to update library access.', 'error');
    } finally {
      setAccessSaving(false);
    }
  };

  const updateEmployeeStatus = (employee, status) => {
    const activate = status === 'active';
    showConfirmationToast(
      `Are you sure you want to ${activate ? 'activate' : 'deactivate'} this employee?${activate ? '' : ' They will be logged out immediately.'}`,
      async () => {
        const id = getUserId(employee);
        setBusyUserId(id);
        try {
          await API.put(`/api/users/${id}`, { status });
          showToast(activate ? 'Employee activated successfully.' : 'Employee deactivated successfully.', 'success');
          await loadData(false);
          if (!activate) {
            try {
              await API.post('/api/auth/logout-user', { userId: id });
            } catch (error) {
              console.error('Employee status changed, but forced logout failed:', error);
              showToast('Employee was deactivated, but the logout could not be forced.', 'error');
            }
          }
        } catch (error) {
          console.error('Failed to update employee status:', error);
          showToast(error.response?.data?.error || 'Failed to update employee status.', 'error');
        } finally {
          setBusyUserId(null);
        }
      },
      () => showToast('Employee status update cancelled.', 'info')
    );
  };

  const deleteEmployee = (employee) => {
    showConfirmationToast(
      `Are you sure you want to permanently delete ${employee.name || employee.username || 'this employee'}?`,
      async () => {
        const id = getUserId(employee);
        setBusyUserId(id);
        try {
          await API.delete(`/api/users/${id}`);
          showToast('Employee deleted successfully.', 'success');
          await loadData(false);
        } catch (error) {
          console.error('Failed to delete employee:', error);
          showToast(error.response?.data?.error || 'Failed to delete employee.', 'error');
        } finally {
          setBusyUserId(null);
        }
      },
      () => showToast('Employee deletion cancelled.', 'info')
    );
  };

  if (loading) return <EmployeesSkeleton canManageUsers={canManageUsers} onAddEmployee={openAdd} />;

  return (
    <div className="employees-page">
      <header className="employees-header">
        <div className="employees-title-wrap">
          <span className="employees-heading-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM20 8v6m3-3h-6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
          </span>
          <div>
          <h1>Employee Management</h1>
          <p>Manage employee accounts, status, and library access.</p>
          </div>
        </div>
        <div className="employee-header-actions">
          {canManageUsers && <button type="button" className="employee-add-button" onClick={openAdd}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>Add Employee</button>}
        </div>
      </header>

      {currentUserRole && !canManageUsers && (
        <div
          className="panel"
          role="note"
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            fontSize: '0.9rem',
            lineHeight: 1.5
          }}
        >
          <b>Limited library scope.</b> You only see employees assigned to your
          libraries. Library access can only be changed by the tenant owner.
        </div>
      )}

      <section className="employee-stats" aria-label="Employee statistics">
        <div className="panel employee-stat"><div>Total Employees<span className="stat-icon purple" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m7-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm11 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg></span></div><b>{enrichedEmployees.length}</b></div>
        <div className="panel employee-stat"><div>Active Employees<span className="stat-icon green" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7"/></svg></span></div><b>{activeCount}</b></div>
        <div className="panel employee-stat"><div>Inactive Employees<span className="stat-icon gray" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M8 11V7a4 4 0 1 1 8 0v4m-9 0h10a2 2 0 0 1 2 2v7H5v-7a2 2 0 0 1 2-2Zm5 4v2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg></span></div><b>{inactiveCount}</b></div>
      </section>

      <div className="filter-toggle-row">
        <button type="button" className="filter-toggle-btn" aria-expanded={filtersOpen} aria-controls="employee-filter-panel" onClick={() => setFiltersOpen(previous => !previous)}>
          <FaFilter aria-hidden="true" /><span>{filtersOpen ? 'Hide filters' : 'Show filters'}</span>
        </button>
      </div>

      {filtersOpen && <section id="employee-filter-panel" className="employee-filter-card" aria-label="Employee filters">
        <div className="employee-filter-heading">
          <div className="employee-filter-title">
            <span className="employee-filter-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M4 5h16l-6.5 7.5V19l-3 1v-7.5L4 5Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg></span>
            <div><b>Filters</b><small>Find employees by name, status, role, or library.</small></div>
          </div>
          <span className="employee-filter-count"><b>{filteredEmployees.length}</b> of <b>{enrichedEmployees.length}</b> employees</span>
        </div>
        {filtersOpen && <>
        <div className="employee-filter-controls">
          <label className="employee-search">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8"/><path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, email, or library..." aria-label="Search employees" />
          </label>
          <div className="employee-filter-select"><span>Status</span><FormSelect className="employee-filter-dropdown" value={statusFilter} onChange={setStatusFilter} ariaLabel="Filter by employee status" options={[{ value: 'all', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} /></div>
          <div className="employee-filter-select"><span>Role</span><FormSelect className="employee-filter-dropdown" value={roleFilter} onChange={setRoleFilter} ariaLabel="Filter by employee role" options={[{ value: 'all', label: 'All Roles' }, ...[...new Set(enrichedEmployees.map(employee => String(employee.role || '').toLowerCase()).filter(Boolean))].map(role => ({ value: role, label: role === 'admin' ? 'Employee' : role.charAt(0).toUpperCase() + role.slice(1) }))]} /></div>
          <div className="employee-filter-select"><span>Library</span><FormSelect className="employee-filter-dropdown" value={libraryFilter} onChange={setLibraryFilter} ariaLabel="Filter by library access" options={[{ value: 'all', label: 'All Libraries' }, { value: 'none', label: 'No Access' }, ...libraries.filter(library => getLibraryId(library) !== undefined && getLibraryId(library) !== null).map(library => ({ value: String(getLibraryId(library)), label: library.library_name || library.name || 'Unnamed library' }))]} /></div>
          <button type="button" className="employee-clear-filters" onClick={() => { setSearch(''); setStatusFilter('all'); setRoleFilter('all'); setLibraryFilter('all'); }} disabled={!hasActiveFilters}>Clear filters</button>
        </div>
        <div className="employee-filter-footer">
          <span>Showing <b>{visibleEmployees.length}</b> of <b>{filteredEmployees.length}</b> employees (Page {currentPage} of {totalPages || 1})</span>
          <div className="employee-rows-select"><span>Rows per page</span><FormSelect className="employee-rows-dropdown" value={itemsPerPage} onChange={(value) => setItemsPerPage(Number(value))} ariaLabel="Rows per page" options={[5, 10, 25, 50].map(value => ({ value, label: String(value) }))} /></div>
        </div>
        </>}
      </section>}

      <section className="panel table-panel">
        <div className="employee-table-heading"><h2>All Employees</h2><span>{filteredEmployees.length} employees</span></div>
        <div className="table-scroll">
          <table className="employees-table">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last Login</th><th>Created</th><th>Library Access</th><th>Actions</th></tr></thead>
            <tbody>
              {visibleEmployees.map((employee) => {
                const employeeRole = String(employee.role || '').toLowerCase();
                const status = String(employee.status || 'active').toLowerCase();
                const id = getUserId(employee);
                const isBusy = sameId(busyUserId, id);
                return (
                  <tr key={employee._uid}>
                    <td><div className="employee-identity"><span className="emp-icon">{(employee.name || employee.username || 'E').trim().charAt(0).toUpperCase()}</span><b>{employee.name || employee.username || 'Unnamed employee'}</b></div></td>
                    <td>{employee.email || '—'}</td>
                    <td><span className={`employee-role ${employeeRole === 'admin' ? 'employee' : ''}`}>{employeeRole === 'admin' ? 'Employee' : employee.role || '—'}</span></td>
                    <td><span className={`status ${status === 'active' ? 'good' : 'muted'}`}>{status === 'active' ? 'Active' : 'Inactive'}</span></td>
                    <td>{formatDate(employee.last_login || employee.lastLogin)}</td>
                    <td>{formatDate(employee.created_at || employee.createdAt)}</td>
                    <td><span className={employee._libraryIds.length ? 'library-access' : 'library-access no-access'} title={employee._hasAllLibraries ? `Access to all ${availableLibraryIds.length} libraries` : employee._libraryNames.length ? employee._libraryNames.join(', ') : 'No library access'}>{employee.library_name_display}</span></td>
                    <td>
                      <div className="employee-actions">
                        {status === 'active' && canManageUsers && <button type="button" className="employee-icon-button warning" aria-label={`Deactivate ${employee.name || 'employee'}`} title="Deactivate" disabled={isBusy} onClick={() => updateEmployeeStatus(employee, 'inactive')}>⏸</button>}
                        {status !== 'active' && (canManageUsers || currentUserRole === 'admin') && <button type="button" className="employee-icon-button success" aria-label={`Activate ${employee.name || 'employee'}`} title="Activate" disabled={isBusy} onClick={() => updateEmployeeStatus(employee, 'active')}>✓</button>}
                        {canManageUsers && employeeRole === 'admin' && <button type="button" className="employee-icon-button" aria-label={`Edit ${employee.name || 'employee'}`} title="Edit employee" disabled={isBusy} onClick={() => openEdit(employee)}>✎</button>}
                        {canManageUsers && employeeRole === 'admin' && <button type="button" className="employee-icon-button" aria-label={`Manage library access for ${employee.name || 'employee'}`} title="Manage library access" disabled={isBusy} onClick={() => openLibraryAccess(employee)}>▤</button>}
                        {canManageUsers && <button type="button" className="employee-icon-button danger" aria-label={`Delete ${employee.name || 'employee'}`} title="Delete employee" disabled={isBusy} onClick={() => deleteEmployee(employee)}>×</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!visibleEmployees.length && <tr><td colSpan={8} className="empty-cell">{hasActiveFilters ? 'No employees match these filters.' : 'No employees found.'}</td></tr>}
            </tbody>
          </table>
        </div>
        <nav className="employee-pagination" aria-label="Employee table pagination">
          <button type="button" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
          <button type="button" onClick={() => setCurrentPage(page => Math.max(1, page - 1))} disabled={currentPage === 1}>Previous</button>
          <span>Page <b>{currentPage}</b> of <b>{totalPages || 1}</b></span>
          <button type="button" onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))} disabled={!totalPages || currentPage >= totalPages}>Next</button>
          <button type="button" onClick={() => setCurrentPage(totalPages)} disabled={!totalPages || currentPage >= totalPages}>Last</button>
        </nav>
      </section>

      {editing && (
        <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !saving && setEditing(null)}>
          <section className="modal employee-editor employee-management-modal">
            <div className="modal-head">
              <div><span className="eyebrow">EMPLOYEE</span><h2>{editing === 'new' ? 'Add Employee' : 'Edit Employee'}</h2></div>
              <button type="button" className="close-button" aria-label="Close employee form" onClick={() => !saving && setEditing(null)}>×</button>
            </div>
            <form onSubmit={saveEmployee}>
              <div className="employee-form-grid">
                <label><span>Name <em>*</em></span><input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
                <label><span>Email <em>*</em></span><input required type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
                <label><span>Role</span><input value="Employee" readOnly /></label>
                <label><span>Status <em>*</em></span><select required value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
                <label className="employee-password-heading"><span>{editing === 'new' ? 'Password' : 'New Password'} {editing === 'new' && <em>*</em>}</span><input type="password" required={editing === 'new'} minLength={6} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder={editing === 'new' ? 'Minimum 6 characters' : 'Leave blank to keep current password'} /></label>
                <div className="employee-library-field">
                  <span>Assign Libraries <small>(Optional)</small></span>
                  <LibraryAccessOptions
                    libraries={libraries}
                    selectedIds={form.library_ids}
                    onToggle={toggleFormLibrary}
                    onToggleAll={toggleAllFormLibraries}
                    idPrefix="employee-form-library"
                  />
                  <small className="employee-help-text">Leave all unchecked to give this employee no library access.</small>
                </div>
              </div>
              <div className="modal-actions"><button type="button" className="button ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</button><button type="submit" className="button primary" disabled={saving}>{saving ? 'Saving...' : editing === 'new' ? 'Create Employee' : 'Save Changes'}</button></div>
            </form>
          </section>
        </div>
      )}

      {accessUser && (
        <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && !accessSaving && setAccessUser(null)}>
          <section className="modal employee-editor employee-access-modal">
            <div className="modal-head">
              <div><span className="eyebrow">LIBRARY ACCESS</span><h2>Manage Library Access</h2><p className="muted-note">Choose which libraries {accessUser.name || accessUser.username} can access.</p></div>
              <button type="button" className="close-button" aria-label="Close library access dialog" onClick={() => !accessSaving && setAccessUser(null)}>×</button>
            </div>
            <LibraryAccessOptions
              libraries={libraries}
              selectedIds={selectedLibraryIds}
              onToggle={toggleAccessLibrary}
              onToggleAll={(checked) => setSelectedLibraryIds(checked ? libraries.map(getLibraryId).filter(id => id !== undefined && id !== null) : [])}
              idPrefix="employee-access-library"
            />
            <p className="employee-help-text">If no library is selected, this employee will have no library access.</p>
            <div className="modal-actions"><button type="button" className="button ghost" onClick={() => setAccessUser(null)} disabled={accessSaving}>Cancel</button><button type="button" className="button primary" onClick={saveLibraryAccess} disabled={accessSaving}>{accessSaving ? 'Saving...' : 'Save Changes'}</button></div>
          </section>
        </div>
      )}
    </div>
  );
}
